import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Clinics from "./pages/Clinics";
import ClinicDetails from "./pages/ClinicDetails";
import InstrumentsRequestPublic from "./pages/InstrumentsRequestPublic";
import Offices from "./pages/Offices";
import OfficeDetails from "./pages/OfficeDetails";
import Restrooms from "./pages/Restrooms";
import RestroomDetails from "./pages/RestroomDetails";
import Facilities from "./pages/Facilities";
import FacilityDetails from "./pages/FacilityDetails";
import MaintenanceRequests from "./pages/MaintenanceRequests";
import SupplyRequests from "./pages/SupplyRequests";
import Schedule from "./pages/Schedule";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import QRScanner from "./pages/QRScanner";
import Evaluations from "./pages/Evaluations";
import Alerts from "./pages/Alerts";
import AdvancedDashboard from "./pages/AdvancedDashboard";
import PublicQRScanner from "./pages/PublicQRScanner";
import PPMVisitPublic from "./pages/PPMVisitPublic";
import PublicFeedback from "./pages/PublicFeedback";
import FeedbackManagement from "./pages/FeedbackManagement";
import ForceChangePassword from "./pages/ForceChangePassword";
import TreatmentPlan from "./pages/TreatmentPlan";
import TreatmentPlans from "./pages/TreatmentPlans";
import DoctorsPage from "./pages/DoctorsPage";
import DoctorPricing from "./pages/DoctorPricing";
import DoctorsManagement from "./pages/DoctorsManagement";
import DoctorsHub from "./pages/DoctorsHub";
import Consents from "./pages/Consents";
import ClinicSchedule from "./pages/ClinicSchedule";
import Sterilization from "./pages/Sterilization";
import InstrumentsInventory from "./pages/InstrumentsInventory";
import ValetPublic from "./pages/ValetPublic";
import ValetAdmin from "./pages/ValetAdmin";
import AutoclaveLog from "./pages/AutoclaveLog";
import AutoclavePrint from "./pages/AutoclavePrint";
import SterilizationKPIs from "./pages/SterilizationKPIs";
import BiomedDevices from "./pages/BiomedDevices";
import PPMContracts from "./pages/PPMContracts";
import PPMCheckin from "./pages/PPMCheckin";
import BiomedDevicePrint from "./pages/BiomedDevicePrint";
import MaintenancePrint from "./pages/MaintenancePrint";
import MedicalReportRequests from "./pages/MedicalReportRequests";

// Components
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Token verification failed:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      setUser(userData);
      const isSterile = ['sterilization_dirty', 'sterilization_packaging', 'sterilization_storage'].includes(userData?.role);
      toast.success(isSterile ? "Logged in successfully" : "تم تسجيل الدخول بنجاح");
      return true;
    } catch (error) {
      const isSterileAttempt = username?.startsWith("sterile_");
      const message = error.response?.data?.detail || (isSterileAttempt ? "Login failed" : "فشل تسجيل الدخول");
      toast.error(message);
      return false;
    }
  };

  const logout = () => {
    const isSterile = ['sterilization_dirty', 'sterilization_packaging', 'sterilization_storage'].includes(user?.role);
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    toast.success(isSterile ? "Logged out" : "تم تسجيل الخروج");
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles, allowBiomedOfficer = false, allowPpmOfficer = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="lucent-loader">
          <img src="/lucent-logo.png" alt="LUCENT" />
          <div className="lucent-loader-bar"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role === 'data_entry' && location.pathname !== '/checkin') {
    return <Navigate to="/checkin" replace />;
  }

  const officerBypass = user && user.role !== 'data_entry' && ((allowBiomedOfficer && user.is_biomed_officer) || (allowPpmOfficer && user.is_ppm_officer));
  if (allowedRoles && !allowedRoles.includes(user.role) && !officerBypass) {
    // Redirect technician to maintenance page instead of dashboard
    if (user.role === 'technician' || user.role === 'maintenance_technician') {
      return <Navigate to="/maintenance" replace />;
    }
    if (['sterilization_dirty', 'sterilization_packaging', 'sterilization_storage'].includes(user.role)) {
      return <Navigate to="/sterilization" replace />;
    }
    if (user.role === 'data_entry') {
      return <Navigate to="/checkin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  
  if (user?.role === 'technician' || user?.role === 'maintenance_technician') {
    return <Navigate to="/maintenance" replace />;
  }
  if (user?.role === 'sterilization_dirty' || user?.role === 'sterilization_packaging' || user?.role === 'sterilization_storage') {
    return <Navigate to="/sterilization" replace />;
  }
  if (user?.role === 'admin_assistant') {
    return <Navigate to="/medical-report-requests" replace />;
  }
  if (user?.role === 'data_entry') {
    return <Navigate to="/checkin" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

// App Routes
const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes - No Auth Required */}
      <Route path="/report" element={<PublicQRScanner />} />
      <Route path="/ppm-visit/:token" element={<PPMVisitPublic />} />
      <Route path="/report/:clinicId" element={<PublicQRScanner />} />
      <Route path="/report/office/:locationId" element={<PublicQRScanner locationType="office" />} />
      <Route path="/report/restroom/:locationId" element={<PublicQRScanner locationType="restroom" />} />
      <Route path="/report/facility/:locationId" element={<PublicQRScanner locationType="facility" />} />
      <Route path="/instruments-request/:clinicId" element={<InstrumentsRequestPublic />} />
      <Route path="/feedback" element={<PublicFeedback />} />
      <Route path="/valet" element={<ValetPublic />} />
      <Route path="/treatment-plan" element={<TreatmentPlan />} />
      <Route path="/consents" element={<Consents />} />
      <Route path="/schedule" element={<ClinicSchedule standalone />} />
      <Route path="/doctors" element={<DoctorsPage />} />
      
      <Route path="/login" element={user ? <Navigate to={
        (user?.role === 'technician' || user?.role === 'maintenance_technician') ? "/maintenance" :
        ['sterilization_dirty', 'sterilization_packaging', 'sterilization_storage'].includes(user?.role) ? "/sterilization" :
        user?.role === 'admin_assistant' ? "/medical-report-requests" :
        user?.role === 'data_entry' ? "/checkin" :
        "/dashboard"
      } replace /> : <Login />} />
      
      <Route path="/change-password" element={user ? <ForceChangePassword /> : <Navigate to="/login" replace />} />

      {/* Standalone print pages — no Layout sidebar */}
      <Route path="/biomed-devices/:deviceId/print" element={
        <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "technician", "maintenance_technician"]} allowBiomedOfficer>
          <BiomedDevicePrint />
        </ProtectedRoute>
      } />
      <Route path="/maintenance/:requestId/print" element={
        <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "it_manager", "medical_manager", "procurement_manager", "technician", "maintenance_technician"]}>
          <MaintenancePrint />
        </ProtectedRoute>
      } />

      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<RoleBasedRedirect />} />
        <Route path="dashboard" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "it_manager", "medical_manager", "procurement_manager", "nurse", "technician", "maintenance_technician", "admin_assistant"]} allowBiomedOfficer allowPpmOfficer>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="clinics" element={<Clinics />} />
        <Route path="clinics/:clinicId" element={<ClinicDetails />} />
        <Route path="offices" element={<Offices />} />
        <Route path="offices/:officeId" element={<OfficeDetails />} />
        <Route path="restrooms" element={<Restrooms />} />
        <Route path="restrooms/:restroomId" element={<RestroomDetails />} />
        <Route path="facilities" element={<Facilities />} />
        <Route path="facilities/:facilityId" element={<FacilityDetails />} />
        <Route path="maintenance" element={<MaintenanceRequests />} />
        <Route path="supplies" element={
          <ProtectedRoute allowedRoles={["admin", "procurement_manager"]}>
            <SupplyRequests />
          </ProtectedRoute>
        } />
        <Route path="schedule" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "it_manager", "medical_manager"]}>
            <Schedule />
          </ProtectedRoute>
        } />
        <Route path="reports" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "medical_manager", "it_manager"]}>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="users" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Users />
          </ProtectedRoute>
        } />
        <Route path="qr-scanner" element={<QRScanner />} />
        <Route path="evaluations" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "it_manager", "medical_manager", "technician", "maintenance_technician"]}>
            <Evaluations />
          </ProtectedRoute>
        } />
        <Route path="alerts" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "it_manager", "medical_manager", "procurement_manager"]}>
            <Alerts />
          </ProtectedRoute>
        } />
        <Route path="advanced-dashboard" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "it_manager", "medical_manager"]}>
            <AdvancedDashboard />
          </ProtectedRoute>
        } />
        <Route path="feedback-management" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "it_manager", "medical_manager", "procurement_manager"]}>
            <FeedbackManagement />
          </ProtectedRoute>
        } />
        <Route path="treatment-plans" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TreatmentPlans />
          </ProtectedRoute>
        } />
        <Route path="doctor-pricing" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <DoctorPricing />
          </ProtectedRoute>
        } />
        <Route path="doctors-management" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <DoctorsManagement />
          </ProtectedRoute>
        } />
        <Route path="doctors-hub" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <DoctorsHub />
          </ProtectedRoute>
        } />
        <Route path="admin-consents" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Consents />
          </ProtectedRoute>
        } />
        <Route path="sterilization" element={
          <ProtectedRoute allowedRoles={["admin", "sterilization_dirty", "sterilization_packaging", "sterilization_storage"]}>
            <Sterilization />
          </ProtectedRoute>
        } />
        <Route path="instruments" element={
          <ProtectedRoute allowedRoles={["admin", "sterilization_storage"]}>
            <InstrumentsInventory />
          </ProtectedRoute>
        } />
        <Route path="valet-admin" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ValetAdmin />
          </ProtectedRoute>
        } />
        <Route path="autoclave" element={
          <ProtectedRoute allowedRoles={["admin", "sterilization_dirty"]}>
            <AutoclaveLog />
          </ProtectedRoute>
        } />
        <Route path="autoclave/print" element={
          <ProtectedRoute allowedRoles={["admin", "sterilization_dirty"]}>
            <AutoclavePrint />
          </ProtectedRoute>
        } />
        <Route path="sterilization-kpis" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <SterilizationKPIs />
          </ProtectedRoute>
        } />
        <Route path="biomed-devices" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager", "technician", "maintenance_technician"]} allowBiomedOfficer>
            <BiomedDevices />
          </ProtectedRoute>
        } />
        <Route path="ppm-contracts" element={
          <ProtectedRoute allowedRoles={["admin", "maintenance_manager"]} allowBiomedOfficer allowPpmOfficer>
            <PPMContracts />
          </ProtectedRoute>
        } />
        <Route path="checkin" element={
          <ProtectedRoute allowedRoles={["admin", "data_entry"]} allowBiomedOfficer>
            <PPMCheckin />
          </ProtectedRoute>
        } />
        <Route path="medical-report-requests" element={
          <ProtectedRoute allowedRoles={["admin", "admin_assistant"]}>
            <MedicalReportRequests />
          </ProtectedRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App" dir="rtl">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-center" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'IBM Plex Sans Arabic, sans-serif',
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
